<template>
  <section class="bg-[#ebdfd3]">
    <div class="max-w-screen-xl px-4 py-8 mx-auto lg:px-12 sm:text-center lg:py-8">
      <div class="mb-4 font-bold text-center uppercase h2" v-html="boldParagraph"></div>
      <div class="text-center textp md:px-20 lg:px-38 xl:px-48" v-html="paragraph"></div>
      <div class="relative mt-4">
        <nuxt-img
          :src="videoPosterSrc"
          alt="image poster"
          class="mx-auto opacity-1"
          width="1040"
          height="585"
          format="webp"
          sizes="xs:330px sm:400px md:734px lg:995pxxl:1024px"
          loading="lazy"
          :class="showVideo ? 'opacity-0 absolute top-0 left-0 right-0 bottom-0 m-auto left-6/12' : 'rounded-3xl'"
        ></nuxt-img>
        <button
          ref="playButton"
          @click="playVid"
          v-if="!showVideo"
          aria-label="Start video"
          class="absolute inset-0 flex flex-col justify-center w-full h-full text-base text-white bg-opacity-75"
        >
          <svg class="w-24 m-auto md:w-32" viewBox="-13 -7 205 151" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g filter="url(#filter0_d_13_831)">
              <rect x="31" y="8" width="120" height="120" rx="60" fill="#FAF7F5" />
              <path
                d="M106.466 65.4079C108.376 66.6081 108.376 69.3919 106.466 70.5921L84.0571 84.6715C82.0185 85.9524 79.3673 84.4871 79.3673 82.0795L79.3673 53.9205C79.3673 51.5129 82.0185 50.0476 84.0571 51.3284L106.466 65.4079Z"
                fill="#ab4f35"
              />
            </g>
            <defs>
              <filter
                id="filter0_d_13_831"
                x="0.387754"
                y="0.653061"
                width="181.224"
                height="181.224"
                filterUnits="userSpaceOnUse"
                color-interpolation-filters="sRGB"
              >
                <feFlood flood-opacity="0" result="BackgroundImageFix" />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset dy="23.2653" />
                <feGaussianBlur stdDeviation="15.3061" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix type="matrix" values="0 0 0 0 0.211765 0 0 0 0 0.2 0 0 0 0 0.14902 0 0 0 0.3 0" />
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_13_831" />
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_13_831" result="shape" />
              </filter>
            </defs>
          </svg>
        </button>

        <div class="max-w-screen-lg m-auto">
          <div id="video-container" class="relative overflow-hidden sm:h-[0px] sm:pb-[56.25%]" v-if="showVideo">
            <video
              ref="videoPlayer"
              id="myVideo"
              :src="currentVideoSrc"
              class="top-0 left-0 w-full h-full sm:absolute rounded-3xl"
              allow="autoplay; fullscreen"
              loop="false"
              preload="none"
              autoplay
              muted
              sizes="xs:303px sm:400px md:750px lg:1023px xl:1100px 2xl:1200px"
              playsinline
              controls=""
            ></video>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>

const showVideo = ref(false);
const videoElement = ref(null);
const videoPlayer = ref(null);
const playButton = ref(null);
const observer = ref(null);
const hasPlayed = ref(false);

const playVid = () => {
  showVideo.value = true;
  nextTick(() => {
    videoElement.value.scrollIntoView({ behavior: 'smooth' });
    if (videoPlayer.value) {
      videoPlayer.value.play();
    }
  });
};

const currentVideoSrc = computed(() => {
  return window.innerWidth <= 512 ? props.videoSrcMobile : props.videoSrc;
});

const handleIntersection = (entries) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      // Play video when visible
      if (showVideo.value && videoPlayer.value) {
        videoPlayer.value.play()
          .then(() => {
            hasPlayed.value = true;
          })
          .catch(err => {
            console.log('Autoplay not allowed:', err);
          });
      }
    } else {
      // Pause video when not visible
      if (videoPlayer.value && !videoPlayer.value.paused) {
        videoPlayer.value.pause();
      }
    }
  });
};

onMounted(() => {
  // Auto-show video container on component load
  showVideo.value = true;
  
  // Set up IntersectionObserver to monitor video visibility
  observer.value = new IntersectionObserver(handleIntersection, {
    threshold: 0.1 // Trigger when at least 10% of the element is visible
  });
  
  nextTick(() => {
    // Once video element is in DOM, observe it
    const videoContainer = document.getElementById('video-container');
    if (videoContainer) {
      observer.value.observe(videoContainer);
    }
    
    if (playButton.value) {
      playButton.value.addEventListener('click', playVid);
    }
  });
});

onBeforeUnmount(() => {
  // Clean up observers
  if (observer.value) {
    const videoContainer = document.getElementById('video-container');
    if (videoContainer) {
      observer.value.unobserve(videoContainer);
    }
    observer.value.disconnect();
  }
});

const props = defineProps({
  boldParagraph: {
    type: String,
    default: 'The Lifeboost "Skip Your Breakfast 15-Day Challenge"',
  },
  paragraph: {
    type: String,
    default: 'Witness the transformative power of the Lifeboost Latte Bundle with our 15-day challenge. Feel the difference in your energy, focus, and well-being, risk-free! Every sip of Lifeboost coffee is a step toward health, vitality, and unparalleled enjoyment.',
  },
  videoPosterSrc: {
    type: String,
    default: '/assets/images/Longevity_Char_Char.png',
  },
  videoSrc: {
    type: String,
    default: 'https://cdn.shopify.com/videos/c/o/v/f113f00d8c5d4d978e2a9a6bff477232.mp4',
  },
  videoSrcMobile: {
    type: String,
    default: 'https://cdn.shopify.com/videos/c/o/v/721c32d91dec4ca88353132ccc95b45c.mp4',
  },
});


defineBuilderComponent({
  friendlyName: 'Text With Video',
  inputs: [
    {
      name: 'boldParagraph',
      type: 'richText',
      defaultValue: 'The Lifeboost "Skip Your Breakfast 15-Day Challenge"',
    },
    {
      name: 'paragraph',
      type: 'richText',
      defaultValue:
        'Witness the transformative power of the Lifeboost Latte Bundle with our 15-day challenge. Feel the difference in your energy, focus, and well-being, risk-free! Every sip of Lifeboost coffee is a step toward health, vitality, and unparalleled enjoyment.',
    },
    {
      name: 'videoPosterSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'svg', 'png'],
      defaultValue: '/assets/images/Longevity_Char_Char.png',
    },
    {
      name: 'videoSrc',
      type: 'text',
      defaultValue: 'https://cdn.shopify.com/videos/c/o/v/f113f00d8c5d4d978e2a9a6bff477232.mp4',
    },
    {
      name: 'videoSrcMobile',
      type: 'text',
      defaultValue: 'https://cdn.shopify.com/videos/c/o/v/721c32d91dec4ca88353132ccc95b45c.mp4',
    },
  ],
});
</script>